import { useEffect, useRef, useState } from 'react';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css'; // disables native scroll-behavior:smooth while Lenis runs

// Motion system inspired by 63.rw: intro loader, Lenis smooth scroll,
// blur-up word reveals, hide-on-scroll navbar, drawn-ellipse hovers, marquees.

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// ── Smooth scroll ──────────────────────────────────────────
let lenisInstance = null;

export function useSmoothScroll() {
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const lenis = new Lenis({
      duration: 1.5, // longer glide, so sections slide in less abruptly
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      touchMultiplier: 2,
    });
    lenisInstance = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisInstance = null; };
  }, []);
}

// Sticky sections report their *stuck* position, not where they sit in the
// document, so anchor scrolling uses the flow positions computed below.
const naturalTops = new WeakMap();

export function smoothScrollTo(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = naturalTops.has(el)
    ? naturalTops.get(el)
    : el.getBoundingClientRect().top + window.scrollY - 60;
  if (lenisInstance) lenisInstance.scrollTo(top);
  else window.scrollTo({ top, behavior: 'smooth' });
}

// ── Stacked sections (63.rw section transitions) ──────────
// Every direct child with `.stack` becomes sticky. A section taller than the
// screen sticks once its *bottom* reaches the bottom of the viewport, and the
// next section (higher z-index) slides up over it. While being covered, the
// section gets `--cover` 0→1 (used for dim + scale); while sliding in it gets
// `--enter` 0→1. Also reports the nav section in view and whether the
// navbar is over a light section.
const IN_FLOW = new Set(['static', 'relative', 'sticky']);
// How long a section stays pinned before the next slides over it,
// as a fraction of the screen height of extra scrolling (0.35 = 35%).
const STACK_HOLD = 0.35;

export function useStackedSections(rootRef, { onActive, onLight } = {}) {
  const cb = useRef({ onActive, onLight });
  cb.current = { onActive, onLight };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reduced = prefersReducedMotion();
    // stacks: sticky sections; tracked: stacks + `.stack-end` sections, which
    // scroll normally (e.g. contact + footer) but still count for the nav.
    let stacks = [];
    let tracked = [];
    const nextOf = new Map();
    let raf = null;

    const layout = () => {
      const vh = window.innerHeight;
      let flowTop = root.getBoundingClientRect().top + window.scrollY;
      stacks = [];
      tracked = [];
      nextOf.clear();
      let prevStack = null;
      Array.from(root.children).forEach(child => {
        if (!IN_FLOW.has(getComputedStyle(child).position)) return;
        if (prevStack) { nextOf.set(prevStack, child); prevStack = null; }
        const isStack = child.classList.contains('stack');
        if (isStack || child.classList.contains('stack-end')) {
          naturalTops.set(child, flowTop);
          tracked.push(child);
        }
        let hold = 0;
        if (isStack) {
          if (!reduced) {
            child.style.top = `${Math.min(0, vh - child.offsetHeight)}px`;
            child.style.zIndex = stacks.length + 1;
            // Pause: extra scroll distance during which the section stays
            // pinned before the next one starts sliding over it. Only for
            // sections at least a screen tall — a shorter one would leave an
            // empty band below itself during the pause.
            hold = child.offsetHeight >= vh ? Math.round(vh * STACK_HOLD) : 0;
            child.style.marginBottom = `${hold}px`;
          }
          stacks.push(child);
          prevStack = child;
        }
        flowTop += child.offsetHeight + hold;
      });
      update();
    };

    const update = () => {
      raf = null;
      const vh = window.innerHeight;
      const y = window.scrollY;
      let active = '';
      let light = false;

      tracked.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (!reduced && stacks.includes(el)) {
          const enter = Math.min(1, Math.max(0, 1 - rect.top / vh));
          // Dim/recede by how much of this section's on-screen part the next
          // section actually covers — not by how far the next one has entered
          // the screen. (For sections shorter than the screen, the next is
          // already visible below them, which used to dim them too early.)
          const next = nextOf.get(el);
          let cover = 0;
          if (next) {
            const visTop    = Math.max(rect.top, 0);
            const visBottom = Math.min(rect.bottom, vh);
            const visible   = Math.max(visBottom - visTop, 1);
            const overlap   = visBottom - Math.max(next.getBoundingClientRect().top, visTop);
            cover = Math.min(1, Math.max(0, overlap / visible));
          }
          el.style.setProperty('--enter', enter.toFixed(3));
          el.style.setProperty('--cover', cover.toFixed(3));
        }
        if (el.id && naturalTops.get(el) <= y + vh * 0.4) active = el.id;
        // later sections sit on top, so the last one under the navbar wins
        if (rect.top <= 40 && rect.bottom > 40) light = el.dataset.theme === 'light';
      });

      cb.current.onActive?.(active);
      cb.current.onLight?.(light);
    };

    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };

    // Re-measure whenever content changes height (API data, images, filters)
    const ro = new ResizeObserver(() => layout());
    const observeAll = () => Array.from(root.children).forEach(c => ro.observe(c));
    observeAll();
    const mo = new MutationObserver(() => { observeAll(); layout(); });
    mo.observe(root, { childList: true });

    layout();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', layout);
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', layout);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [rootRef]);
}

export function lockScroll(locked) {
  if (lenisInstance) locked ? lenisInstance.stop() : lenisInstance.start();
}

// ── Reveal on scroll ───────────────────────────────────────
// Watches every `.reveal` inside the root, including elements rendered
// later (after API data arrives), and adds `.visible` when they enter view.
export function useRevealAll(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    const seen = new WeakSet();
    const scan = () => root.querySelectorAll('.reveal:not(.visible)').forEach(el => {
      if (!seen.has(el)) { seen.add(el); io.observe(el); }
    });
    scan();

    const mo = new MutationObserver(scan);
    mo.observe(root, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); };
  }, [rootRef]);
}

// ── Intro loader ───────────────────────────────────────────
const INTRO_KEY = 'boom_intro_seen';

export function useIntro() {
  const [show] = useState(() => {
    if (prefersReducedMotion()) return false;
    try { return !sessionStorage.getItem(INTRO_KEY); } catch { return true; }
  });
  return show;
}

// Timeline: monogram rises out of a blur → wordmark "types" in left-to-right
// behind a blinking cursor → tagline fades up → whole screen blurs away.
export function IntroLoader({ onDone }) {
  const wordDelay = 0.9;   // wordmark wipe starts
  const wordDur   = 0.9;   // wipe duration
  const tagDelay  = wordDelay + wordDur + 0.1;
  const total = (tagDelay + 1.1) * 1000;

  const [leaving, setLeaving] = useState(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    lockScroll(true);
    const t1 = setTimeout(() => setLeaving(true), total);
    const t2 = setTimeout(() => {
      try { sessionStorage.setItem(INTRO_KEY, '1'); } catch {}
      document.body.style.overflow = '';
      lockScroll(false);
      doneRef.current?.();
    }, total + 700);
    return () => { clearTimeout(t1); clearTimeout(t2); document.body.style.overflow = ''; lockScroll(false); };
  }, [total]);

  return (
    <div className={`intro${leaving ? ' intro-leave' : ''}`} aria-hidden="true">
      <div className="intro-inner" style={{ '--wd': `${wordDelay}s`, '--wdur': `${wordDur}s` }}>
        <img className="intro-logo-mark" src="/brand/mark-white.png" alt="" />
        <div className="intro-wordmark">
          <img src="/brand/wordmark-white.png" alt="" />
          <span className="intro-cursor" />
        </div>
        <p className="intro-sub" style={{ animationDelay: `${tagDelay}s` }}>
          Innovative ideas for you
        </p>
      </div>
    </div>
  );
}

// ── Infinite marquee row ───────────────────────────────────
export function Marquee({ items, renderItem, direction = 'left', speed = 40 }) {
  // Repeat the list so one copy is always wider than the screen.
  const minCopies = Math.max(2, Math.ceil(8 / Math.max(items.length, 1)));
  const copy = Array.from({ length: minCopies }, () => items).flat();
  const duration = Math.max(20, copy.length * (100 / speed) * 2);
  return (
    <div className={`marquee marquee-${direction}`} style={{ '--marquee-duration': `${duration}s` }}>
      <div className="marquee-track">
        {[0, 1].map(dup => (
          <div className="marquee-group" key={dup} aria-hidden={dup === 1}>
            {copy.map((item, i) => (
              <div className="marquee-item" key={`${dup}-${i}`}>{renderItem(item)}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
